import { ThemeProvider } from '@mui/material/styles';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import App from './App';
import { QueryClient, QueryCache, MutationCache, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from './app/components/ErrorBoundary';
import { reportCacheError } from '@config/errorReporting';
import { persistor, store } from './app/redux/store';
import {
  synchronizeTokensAfterRehydrate,
  synchronizeOrganizationIdAfterRehydrate,
  getTokenLocalStorage,
  getTokenSessionStorage,
} from '@config/storage';
import { setUserData } from '@redux/reducers/user.reducer';
// Nunito, self-hosted via @fontsource (no external CDN request).
// Latin subset only — covers English and Spanish, including accents and ñ.
// Weights match the ones the app actually uses: 400 / 500 / 600 / 700.
import '@fontsource/nunito/latin-400.css';
import '@fontsource/nunito/latin-400-italic.css';
import '@fontsource/nunito/latin-500.css';
import '@fontsource/nunito/latin-600.css';
import '@fontsource/nunito/latin-700.css';
import './index.css';
import reportWebVitals from './reportWebVitals';
import theme from './theme';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Z_INDEX } from '@constants/zIndex';
import ActionMessageBridge from '@components/atoms/ActionMessageBridge';
import {
  ThemeProvider as AppThemeProvider,
  useTheme as useThemeMode,
} from './app/components/layout/ThemeContext';

if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  console.error = () => {};
  console.debug = () => {};
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

const ThemedToasts = () => {
  const { isDarkMode } = useThemeMode();
  return (
    <ToastContainer
      draggable
      rtl={false}
      newestOnTop
      pauseOnHover
      closeOnClick
      pauseOnFocusLoss
      autoClose={5000}
      position="top-right"
      hideProgressBar={false}
      theme={isDarkMode ? 'dark' : 'light'}
      style={{ zIndex: Z_INDEX.TOAST }}
    />
  );
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) =>
      reportCacheError(error, String(query.queryKey[0] ?? 'unknown')),
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) =>
      reportCacheError(error, String(mutation.options.mutationKey?.[0] ?? 'unknown')),
  }),
});
root.render(
  <ErrorBoundary>
    <Provider store={store}>
      <PersistGate
        loading={<div>Loading...</div>}
        persistor={persistor}
        onBeforeLift={() => {
          const state = store.getState();
          const rememberMe = state?.user?.rememberMe ?? false;
          synchronizeTokensAfterRehydrate(rememberMe);
          synchronizeOrganizationIdAfterRehydrate(rememberMe);
          try {
            const localToken = getTokenLocalStorage();
            const sessionToken = getTokenSessionStorage();
            if (localToken && !sessionToken && !rememberMe) {
              store.dispatch(setUserData(localToken, true));
            }
          } catch {
            console.log('error in synchronizeTokensAfterRehydrate');
          }
        }}>
        <ThemeProvider theme={theme}>
          <AppThemeProvider>
            <QueryClientProvider client={queryClient}>
              <ThemedToasts />
              <ActionMessageBridge />
              <App />
            </QueryClientProvider>
          </AppThemeProvider>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  </ErrorBoundary>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
